import React, { Component } from "react";
import moment from "moment";
import * as Cookies from "es-cookie";
import { Row, Col } from "react-bootstrap";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TabSection from "./TabSection";
import { PageShimmer } from "./Shimmer";

const GET_FEE_REPORT = gql`
  query ($mobile: String, $month: String, $academic_year_id: Int) {
    getStudentAttendanceFeeReport(
      mobile: $mobile
      month: $month
      academic_year_id: $academic_year_id
    ) {
      fees {
        total_fees {
          fee_type
          pending_amount
          total_amount
        }
        paid_fees {
          fee_type
          paid_amount
          paid_date
        }
        due_fees {
          fee_type
          due_amount
          due_date
        }
      }
    }
  }
`;

const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};

const round2 = (v) => Math.round(num(v) * 100) / 100;

const money = (v) => `₹${num(v).toLocaleString("en-IN")}`;

class FeesTab extends Component {
    constructor(props) {
        super(props);
        this.state = { fees: null, loading: false };
    }

    componentDidMount() {
        this.getReport();
    }

    getReport = async () => {
        this.setState({ loading: true });
        try {
            const result = await this.props.client.query({
                query: GET_FEE_REPORT,
                variables: {
                    mobile: `${Cookies.get("mobile") || ""}`,
                    month: moment().format("YYYY-MM"),
                    academic_year_id: null,
                },
                fetchPolicy: "network-only",
            });
            const data = result?.data?.getStudentAttendanceFeeReport;
            this.setState({ fees: data?.fees || null, loading: false });
        } catch (error) {
            console.log(error);
            this.setState({ fees: null, loading: false });
        }
    };

    // Fee split donut — paid vs due vs pending.
    renderDonut = (paid, due, pending) => {
        const options = {
            chart: { type: "pie", backgroundColor: "transparent", height: 230 },
            title: { text: "" },
            credits: { enabled: false },
            tooltip: { enabled: true, pointFormat: "<b>{point.y}</b>" },
            plotOptions: {
                pie: {
                    innerSize: "62%",
                    borderWidth: 0,
                    dataLabels: { enabled: true, format: "{point.name}", style: { fontSize: "11px", color: "#64748b" } },
                },
            },
            series: [
                {
                    animation: { duration: 900 },
                    data: [
                        { name: "Paid", y: paid, color: "#22c55e" },
                        { name: "Due", y: due, color: "#ef4444" },
                        { name: "Pending", y: pending, color: "#f59e0b" },
                    ].filter((d) => d.y > 0),
                },
            ],
        };
        return <HighchartsReact highcharts={Highcharts} options={options} />;
    };

    renderTable = (title, rows, cols) => (
        <div className="sp-af-card sp-animate p-3 mb-4" style={{ animationDelay: "120ms" }}>
            <h6 className="font-weight-bold text-dark mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h6>
            {rows && rows.length ? (
                <div className="table-responsive">
                    <table className="table table-borderless mb-0" style={{ fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                                {cols.map((c) => (
                                    <th key={c.key} className={`text-muted ${c.align || ""}`} style={{ fontSize: 11, textTransform: "uppercase" }}>{c.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={i} style={{ borderBottom: "1px solid #f6f8fb" }}>
                                    {cols.map((c) => (
                                        <td key={c.key} className={`${c.align || ""} ${c.bold ? "font-weight-bold text-dark" : "text-muted"}`}>
                                            {c.render ? c.render(r[c.key], r) : (r[c.key] != null && r[c.key] !== "" ? r[c.key] : "-")}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center text-muted py-3" style={{ fontSize: 13 }}>No records.</div>
            )}
        </div>
    );

    renderBody = (fees) => {
        if (!fees) {
            return <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>No fee data available.</div>;
        }
        const total = round2((fees.total_fees || []).reduce((s, r) => s + num(r.total_amount), 0));
        const pending = round2((fees.total_fees || []).reduce((s, r) => s + num(r.pending_amount), 0));
        const paid = round2((fees.paid_fees || []).reduce((s, r) => s + num(r.paid_amount), 0));
        const due = round2((fees.due_fees || []).reduce((s, r) => s + num(r.due_amount), 0));

        const stats = [
            { label: "Total Fees", value: money(total), icon: "fa-file-invoice-dollar", bg: "linear-gradient(135deg,#6366f1,#4f46e5)" },
            { label: "Paid", value: money(paid), icon: "fa-check-circle", bg: "linear-gradient(135deg,#22c55e,#16a34a)" },
            { label: "Due", value: money(due), icon: "fa-exclamation-circle", bg: "linear-gradient(135deg,#ef4444,#dc2626)" },
            { label: "Pending", value: money(pending), icon: "fa-hourglass-half", bg: "linear-gradient(135deg,#f59e0b,#f97316)" },
        ];

        return (
            <>
                <Row>
                    {stats.map((s, i) => (
                        <Col md={3} sm={6} key={s.label} className="mb-3">
                            <div className="sp-af-card sp-animate d-flex align-items-center p-3" style={{ gap: 14, animationDelay: `${i * 70}ms` }}>
                                <div className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                                    style={{ width: 46, height: 46, borderRadius: 12, background: s.bg }}>
                                    <i className={`fas ${s.icon}`} style={{ fontSize: 17 }} />
                                </div>
                                <div>
                                    <div className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>{s.label}</div>
                                    <div className="text-dark" style={{ fontSize: 20, fontWeight: 700, fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>{s.value}</div>
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>
                <Row>
                    <Col lg={4} className="mb-4 mb-lg-0">
                        <div className="sp-af-card sp-animate p-4 h-100" style={{ animationDelay: "120ms" }}>
                            <h6 className="font-weight-bold text-dark mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Fee Breakdown</h6>
                            {this.renderDonut(paid, due, pending)}
                        </div>
                    </Col>
                    <Col lg={8}>
                        {this.renderTable("Total Fees", fees.total_fees, [
                            { key: "fee_type", label: "Fee Type", bold: true },
                            { key: "total_amount", label: "Total", align: "text-right", render: money },
                            { key: "pending_amount", label: "Pending", align: "text-right", render: (v) => <span style={{ color: "#f59e0b", fontWeight: 600 }}>{money(v)}</span> },
                        ])}
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        {this.renderTable("Paid Fees", fees.paid_fees, [
                            { key: "fee_type", label: "Fee Type", bold: true },
                            { key: "paid_amount", label: "Paid", align: "text-right", render: (v) => <span style={{ color: "#16a34a", fontWeight: 600 }}>{money(v)}</span> },
                            { key: "paid_date", label: "Date", align: "text-right" },
                        ])}
                    </Col>
                    <Col md={6}>
                        {this.renderTable("Due Fees", fees.due_fees, [
                            { key: "fee_type", label: "Fee Type", bold: true },
                            { key: "due_amount", label: "Due", align: "text-right", render: (v) => <span style={{ color: "#dc2626", fontWeight: 600 }}>{money(v)}</span> },
                            { key: "due_date", label: "Due Date", align: "text-right" },
                        ])}
                    </Col>
                </Row>
            </>
        );
    };

    render() {
        const { fees, loading } = this.state;
        return (
            <TabSection
                icon="far fa-rupee-sign"
                title="Fees"
                desc="Fee payment status and outstanding dues."
            >
                {loading ? <PageShimmer /> : this.renderBody(fees)}
            </TabSection>
        );
    }
}

export default compose(withApollo, withRouter)(FeesTab);
